import {
  Questionnaire,
  QuestionnaireType,
} from "../../../../../../../api/graphql/schema.generated";
import {
  getSupportedQuestionnairesInfoForYear,
  isShownOnlyIfMatch,
} from "../utils";
import { QuestionnaireInfo } from "./constants";

export interface QuestionnaireEntry {
  key: string;
  info: QuestionnaireInfo;
  questionnaire: Questionnaire | undefined;
}

/**
 * The questionnaires the overview shows for a tax year, in display order: one
 * entry per questionnaire of a type, or a single entry without questionnaire
 * when none exists yet (unless the type is shown only on a match). Shared by
 * the cards and the completeness overview so both always list the same rows.
 */
export const getQuestionnaireEntries = ({
  taxYear,
  questionnaires,
  unavailableTypes,
}: {
  taxYear: number;
  questionnaires: Questionnaire[];
  unavailableTypes: QuestionnaireType[];
}): QuestionnaireEntry[] =>
  getSupportedQuestionnairesInfoForYear(taxYear).flatMap<QuestionnaireEntry>(
    (info, infoIndex) => {
      const matched = questionnaires.filter(({ type }) => type === info.type);

      if (!matched.length && !isShownOnlyIfMatch(info, unavailableTypes)) {
        return [
          {
            key: `${info.type}_${infoIndex}`,
            info,
            questionnaire: undefined,
          },
        ];
      }

      return matched.map((questionnaire, subIndex) => ({
        key: `${info.type}_${infoIndex}_${subIndex}`,
        info,
        questionnaire,
      }));
    }
  );

/** The line a card shows under its title (child questionnaires name the child). */
export const getQuestionnaireDescription = (
  info: QuestionnaireInfo,
  questionnaire?: Questionnaire
): string => {
  // context is nullable: name the child only when we know the name
  const firstName =
    questionnaire?.type === QuestionnaireType.EOY_INCOME_TAX_CHILD
      ? questionnaire.context?.firstName
      : undefined;

  return firstName ? `${info.description}: ${firstName}` : info.description;
};
